import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

interface CountdownProps {
  endTime: string;
  showZeroDays?: boolean;
  onEnd?: () => void;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const CountdownWidget: React.FC<CountdownProps> = ({ endTime, showZeroDays = true, onEnd }) => {

  const calculate = useCallback((): TimeLeft => {
    const now = dayjs();
    const target = dayjs(endTime);
    const diff = target.diff(now);
    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const dur = dayjs.duration(diff);

    const days = Math.floor(dur.asDays());
    const hours = Math.floor(dur.hours());
    const minutes = Math.floor(dur.minutes());
    const seconds = Math.floor(dur.seconds());

    return { days, hours, minutes, seconds };
  }, [endTime]);

  const [time, setTime] = useState<TimeLeft>(calculate());

  useEffect(() => {
    const i = setInterval(() => {
      const newTime = calculate();
      if (newTime.days === 0 && newTime.hours === 0 && newTime.minutes === 0 && newTime.seconds === 0) {
        clearInterval(i);
        onEnd?.();
      } else {
        setTime(newTime);
      }
    }, 1000);
    return () => clearInterval(i);
    // react-hooks/exhaustive-deps
  }, [endTime, calculate, onEnd]);

  const renderTimeBox = (label: string, value: number) => {
    return <Box sx={{ textAlign: "center", mx: 'auto' }}>
      <Box 
        sx={{
          maxWidth: '45px',
          bgcolor: "rgba(255,255,255,0.15)",
          px: 1.5,
          py: 1,
          borderRadius: 2,
          fontWeight: 700,
          fontSize: 18,
          border: '0.05rem solid #ffffff5c'
        }}>
        {String(value).padStart(2, "0")}
      </Box>
      <Typography variant="caption" sx={{ opacity: 0.9 }}>{label}</Typography>
    </Box>
  }

  return (
    <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
      {time.days > 0
        ? renderTimeBox("DAYS", time.days)
        : showZeroDays ? renderTimeBox("DAYS", time.days) : null
      }
      {renderTimeBox("HOURS", time.hours)}
      {renderTimeBox("MINs", time.minutes)}
      {renderTimeBox("SECS", time.seconds)}
    </Box>
  );
};

export default CountdownWidget;